import { ChevronDown, ChevronUp, LayoutGrid, Loader2, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useConfirm, useToast } from "@/components/shared";
import { appService, teamService } from "@/lib/services";
import type { App, TeamHub as TeamHubType } from "@/types";
import { AppIconImage } from "./AppIconImage";
import { FormField } from "./FormWidgets";
import { resolveIcon } from "./utils";

export function TeamAppsSection({
  hub,
  onRefresh,
}: Readonly<{
  hub: TeamHubType;
  onRefresh: () => void;
}>) {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [expanded, setExpanded] = useState(true);
  const [adding, setAdding] = useState(false);
  const [allApps, setAllApps] = useState<App[]>([]);
  const [loadingApps, setLoadingApps] = useState(false);
  const [selectedAppUuid, setSelectedAppUuid] = useState("");
  const [saving, setSaving] = useState(false);
  const [removingUuid, setRemovingUuid] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);

  const teamApps = hub.apps ?? [];
  const assignedUuids = useMemo(() => new Set(teamApps.map((a) => a.uuid)), [teamApps]);

  const loadAllApps = async () => {
    setLoadingApps(true);
    try {
      const response = await appService.list();
      setAllApps(response.data ?? []);
    } catch (e) {
      console.error("Failed to load apps:", e);
      showToast("Failed to load apps", "error");
    } finally {
      setLoadingApps(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedAppUuid) return;
    setSaving(true);
    try {
      await teamService.assignApp(hub.uuid, {
        appUuid: selectedAppUuid,
        displayOrder: teamApps.length,
      });
      setAdding(false);
      setSelectedAppUuid("");
      onRefresh();
    } catch (e) {
      console.error("Failed to assign app:", e);
      showToast("Failed to assign app", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (appUuid: string) => {
    const confirmed = await confirm({
      title: "Remove App",
      message: "Are you sure you want to remove this app from the team?",
      confirmText: "Remove",
      variant: "danger",
    });
    if (!confirmed) return;

    setRemovingUuid(appUuid);
    try {
      await teamService.removeApp(hub.uuid, appUuid);
      onRefresh();
    } catch (e) {
      console.error("Failed to remove app:", e);
      showToast("Failed to remove app", "error");
    } finally {
      setRemovingUuid(null);
    }
  };

  const handleReorder = async (appUuid: string, direction: "up" | "down") => {
    if (reordering) return;
    const idx = teamApps.findIndex((a) => a.uuid === appUuid);
    if (idx < 0) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= teamApps.length) return;
    setReordering(true);
    try {
      await Promise.all([
        teamService.updateApp(hub.uuid, teamApps[idx].uuid, {
          displayOrder: teamApps[swapIdx].displayOrder,
        }),
        teamService.updateApp(hub.uuid, teamApps[swapIdx].uuid, {
          displayOrder: teamApps[idx].displayOrder,
        }),
      ]);
      onRefresh();
    } catch (e) {
      console.error("Failed to reorder:", e);
      showToast("Failed to reorder", "error");
    } finally {
      setReordering(false);
    }
  };

  const availableApps = useMemo(() => allApps.filter((a) => !assignedUuids.has(a.uuid)), [allApps, assignedUuids]);

  return (
    <div className="bg-surface-primary border border-border-primary rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-5 hover:bg-interactive-hover transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-cyan-500/20 flex items-center justify-center">
            <LayoutGrid className="h-5 w-5 text-cyan-400" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-semibold text-content-primary">Team Apps</h3>
            <p className="text-xs text-content-tertiary">
              {teamApps.length} {teamApps.length === 1 ? "app" : "apps"} assigned
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-content-quaternary" />
        ) : (
          <ChevronDown className="h-4 w-4 text-content-quaternary" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-border-primary">
          {teamApps.length > 0 && (
            <div className="divide-y divide-border-primary">
              {teamApps.map((app, idx) => {
                const Icon = resolveIcon(app.icon);
                return (
                  <div key={app.uuid} className="flex items-center gap-4 px-5 py-3.5 group">
                    <div className="flex flex-col gap-0.5">
                      <button
                        onClick={() => handleReorder(app.uuid, "up")}
                        disabled={idx === 0}
                        className="p-0.5 text-content-quaternary hover:text-content-secondary disabled:opacity-20 transition-colors"
                      >
                        <ChevronUp className="h-3 w-3" />
                      </button>
                      <button
                        onClick={() => handleReorder(app.uuid, "down")}
                        disabled={idx === teamApps.length - 1}
                        className="p-0.5 text-content-quaternary hover:text-content-secondary disabled:opacity-20 transition-colors"
                      >
                        <ChevronDown className="h-3 w-3" />
                      </button>
                    </div>
                    {app.iconUrl ? (
                      <AppIconImage
                        src={app.iconUrl}
                        color={app.color || "#EC622B"}
                        sizeClasses="h-9 w-9 rounded-lg"
                        iconSizeClasses="h-4 w-4"
                      />
                    ) : (
                      <div className="h-9 w-9 rounded-lg bg-linear-to-br from-mutual-orange/20 to-mutual-purple/20 flex items-center justify-center shrink-0">
                        <Icon className={`h-4 w-4 ${app.color || "text-brand-primary"}`} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-content-primary truncate">{app.name}</p>
                      {app.description && (
                        <p className="text-xs text-content-tertiary truncate mt-0.5">{app.description}</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleRemove(app.uuid)}
                      disabled={removingUuid === app.uuid}
                      className="p-1.5 rounded-md hover:bg-red-500/10 transition-colors text-content-tertiary hover:text-red-400 opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {adding ? (
            <div className="p-5 border-t border-border-primary bg-surface-secondary/20">
              {loadingApps ? (
                <div className="flex items-center gap-2 text-sm text-content-tertiary">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading available apps...
                </div>
              ) : (
                <>
                  <FormField label="Select an app to add">
                    <select
                      value={selectedAppUuid}
                      onChange={(e) => setSelectedAppUuid(e.target.value)}
                      className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-sm text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
                    >
                      <option value="">Choose an app...</option>
                      {availableApps.map((a) => (
                        <option key={a.uuid} value={a.uuid}>
                          {a.name}
                          {a.description ? ` — ${a.description}` : ""}
                        </option>
                      ))}
                    </select>
                  </FormField>
                  {availableApps.length === 0 && (
                    <p className="text-xs text-content-quaternary mt-2">
                      All available apps are already assigned to this team.
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-4">
                    <button
                      onClick={handleAssign}
                      disabled={saving || !selectedAppUuid}
                      className="px-4 py-2 bg-brand-gradient text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40 flex items-center gap-1.5"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      {saving ? "Adding..." : "Add App"}
                    </button>
                    <button
                      onClick={() => {
                        setAdding(false);
                        setSelectedAppUuid("");
                      }}
                      className="px-4 py-2 bg-surface-secondary text-content-secondary rounded-lg text-sm font-medium hover:bg-interactive-active transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="p-4 border-t border-border-primary">
              <button
                onClick={() => {
                  setAdding(true);
                  loadAllApps();
                }}
                className="flex items-center gap-2 px-3 py-2 text-sm text-brand-primary hover:bg-brand-primary/10 rounded-lg transition-colors font-medium"
              >
                <Plus className="h-4 w-4" />
                Add App to Team
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
